/**
 * src/scripts/components/dyad-waitlist.js
 *
 * "Waitlist" button on a full Dyad event card (views/partials/event-list.ejs)
 * — collects name + email and POSTs to /dyad-registration/waitlist. Event-
 * delegated from document, since event-list.ejs (and therefore any number of
 * "Waitlist" buttons) can appear on many different pages/sections. Bundled
 * into main.js (auto-globbed from src/scripts/components/), so this runs on
 * every page without per-page wiring.
 *
 * Popup follows this repo's standard fixed-overlay pattern (see CLAUDE.md) —
 * not window.prompt().
 */

(function () {
    'use strict';

    var RECAPTCHA_ACTION = 'dyad_waitlist';

    function esc(str) {
        if (str == null) return '';
        return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    }

    function getCsrfToken() {
        var meta = document.querySelector('meta[name="csrf-token"]');
        return meta ? meta.content : '';
    }

    function injectRecaptchaScript(siteKey) {
        if (document.querySelector('script[src*="recaptcha/api.js"]')) return;
        var script = document.createElement('script');
        script.src = 'https://www.google.com/recaptcha/api.js?render=' + encodeURIComponent(siteKey);
        script.async = true;
        document.head.appendChild(script);
    }

    function getRecaptchaToken(siteKey) {
        if (!siteKey) return Promise.resolve(null);
        return new Promise(function (resolve) {
            var settled = false;
            var finish = function (value) {
                if (settled) return;
                settled = true;
                resolve(value);
            };

            var tryExecute = function () {
                if (typeof grecaptcha === 'undefined') return false;
                grecaptcha.ready(function () {
                    grecaptcha.execute(siteKey, { action: RECAPTCHA_ACTION }).then(finish).catch(function () {
                        finish(null);
                    });
                });
                return true;
            };

            if (tryExecute()) return;

            var poll = setInterval(function () {
                if (tryExecute()) clearInterval(poll);
            }, 50);
            setTimeout(function () {
                clearInterval(poll);
                finish(null);
            }, 8000);
        });
    }

    var RECAPTCHA_NOTICE =
        '<p class="recaptcha-disclosure">This site is protected by reCAPTCHA and the Google ' +
        '<a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy<span class="visually-hidden"> (opens in a new tab)</span></a> and ' +
        '<a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service<span class="visually-hidden"> (opens in a new tab)</span></a> apply.</p>';

    function closeModal(overlay) {
        overlay.remove();
        document.removeEventListener('keydown', overlay._escHandler);
    }

    function openModal(sessionId, sessionLabel, recaptchaSiteKey) {
        if (recaptchaSiteKey) injectRecaptchaScript(recaptchaSiteKey);

        var openedAt = Date.now();

        var overlay = document.createElement('div');
        overlay.className = 'dyad-waitlist-overlay';
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-modal', 'true');
        overlay.innerHTML =
            '<div class="dyad-waitlist-modal">' +
                '<h3>Join the Waitlist</h3>' +
                '<p>' + esc(sessionLabel || 'This session') + ' is full. We\'ll email you if a spot opens up.</p>' +
                '<div class="dyad-waitlist-modal__error is-hidden" role="alert"></div>' +
                '<form class="dyad-waitlist-modal__form">' +
                    '<div class="dyad-waitlist-modal__field">' +
                        '<label for="dwl-name">Full Name</label>' +
                        '<input type="text" id="dwl-name" name="name" required autocomplete="name">' +
                    '</div>' +
                    '<div class="dyad-waitlist-modal__field">' +
                        '<label for="dwl-email">Email</label>' +
                        '<input type="email" id="dwl-email" name="email" required autocomplete="email">' +
                    '</div>' +
                    '<div class="dyad-waitlist-modal__actions">' +
                        '<button type="button" class="btn btn--secondary" data-dwl-cancel>Cancel</button>' +
                        '<button type="submit" class="btn btn--primary" data-dwl-submit>Join Waitlist</button>' +
                    '</div>' +
                    // Required wherever reCAPTCHA protects a form, since the badge is hidden sitewide
                    (recaptchaSiteKey ? RECAPTCHA_NOTICE : '') +
                '</form>' +
            '</div>';

        document.body.appendChild(overlay);

        var form = overlay.querySelector('.dyad-waitlist-modal__form');
        var errorBox = overlay.querySelector('.dyad-waitlist-modal__error');
        var submitBtn = overlay.querySelector('[data-dwl-submit]');
        var cancelBtn = overlay.querySelector('[data-dwl-cancel]');
        var nameInput = overlay.querySelector('#dwl-name');

        function showError(message) {
            errorBox.textContent = message;
            errorBox.classList.remove('is-hidden');
        }

        cancelBtn.addEventListener('click', function () {
            closeModal(overlay);
        });
        overlay.addEventListener('click', function (e) {
            if (e.target === overlay) closeModal(overlay);
        });
        overlay._escHandler = function (e) {
            if (e.key === 'Escape') closeModal(overlay);
        };
        document.addEventListener('keydown', overlay._escHandler);

        form.addEventListener('submit', function (e) {
            e.preventDefault();
            errorBox.classList.add('is-hidden');
            submitBtn.disabled = true;
            cancelBtn.disabled = true;
            submitBtn.textContent = 'Joining…';

            getRecaptchaToken(recaptchaSiteKey).then(function (recaptchaToken) {
                return fetch('/dyad-registration/waitlist', {
                    method: 'POST',
                    credentials: 'same-origin',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        'x-csrf-token': getCsrfToken(),
                    },
                    body: JSON.stringify({
                        dyadSessionId: sessionId,
                        name: form.elements.name.value,
                        email: form.elements.email.value,
                        recaptchaToken: recaptchaToken,
                        _wl_ts: openedAt,
                    }),
                });
            }).then(function (response) {
                return response.json().then(function (data) {
                    return { ok: response.ok, data: data };
                });
            }).then(function (res) {
                if (!res.ok || !res.data.success) {
                    throw new Error(res.data.message || 'Could not join the waitlist. Please try again.');
                }
                overlay.querySelector('.dyad-waitlist-modal').innerHTML =
                    '<h3>You\'re on the Waitlist</h3>' +
                    '<p>We\'ll email you at ' + esc(form.elements.email.value) + ' if a spot opens up.</p>' +
                    '<div class="dyad-waitlist-modal__actions">' +
                        '<button type="button" class="btn btn--primary" data-dwl-close>Close</button>' +
                    '</div>';
                overlay.querySelector('[data-dwl-close]').addEventListener('click', function () {
                    closeModal(overlay);
                });
            }).catch(function (err) {
                showError(err.message || 'Something went wrong. Please try again.');
                submitBtn.disabled = false;
                cancelBtn.disabled = false;
                submitBtn.textContent = 'Join Waitlist';
            });
        });

        nameInput.focus();
    }

    document.addEventListener('click', function (e) {
        var btn = e.target.closest('.js-dyad-waitlist-btn');
        if (!btn) return;

        var sessionId = btn.getAttribute('data-session-id');
        var sessionLabel = btn.getAttribute('data-session-label');
        var container = btn.closest('.event-list-section');
        var recaptchaSiteKey = container ? container.getAttribute('data-recaptcha-site-key') : '';

        openModal(sessionId, sessionLabel, recaptchaSiteKey);
    });
})();
